import React, { useMemo, useState } from "react";

import { useDeleteTask } from "api/api-hooks/airflow-hooks";
import ActionButtons from "components/basic/action-buttons/ActionButtons";
import CheckIcon from "components/basic/check-icon/CheckIcon";
import CollapsibleText from "components/basic/collapsible-text/CollapsibleText";
import DataTable from "components/basic/data-table/DataTable";
import UserAvatar from "components/basic/user-avatar/UserAvatar";
import { TableColumn } from "components/pages/measurement-devices/utils/types";
import { useHasPermissions } from "hooks/useHasPermissions";
import { useTimezone } from "hooks/useTimezone";
import { DataTableProps } from "primereact/datatable";
import { Tag } from "primereact/tag";
import { Link } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { AirflowDag, AirflowTaskDto, AirflowTaskFilter, airflowTaskTypes } from "types/airflow";
import { fmtDatetime } from "utils/time-utils";
import TasksHeader from "./TasksHeader";

type AirflowTaskTableProps = {
  tasks: AirflowTaskDto[];
  dag?: AirflowDag;
  loading?: boolean;
};

const AirflowTaskTable: React.FC<AirflowTaskTableProps> = ({ tasks, dag, loading }) => {
  const [filter, setFilter] = useState<AirflowTaskFilter>({
    search: "",
    filters: [],
  });

  const deleteTask = useDeleteTask();

  const canEdit = useHasPermissions("AIRFLOW_EDIT");
  const tz = useTimezone();

  const renderActions = (rowData: AirflowTaskDto) => {
    return (
      <ActionButtons
        onClickDelete={() => deleteTask.mutate({ dagId: dag!.dagId, taskId: rowData.id.taskId })}
      />
    );
  };

  const renderTaskTags = (streams: string[], prefix: "up" | "down") => (
    <div className="flex flex-column gap-1">
      {streams.length === 0 && <span>{`No ${prefix}stream tasks`}</span>}
      {streams.map(id => (
        <Tag key={id} value={id} />
      ))}
    </div>
  );

  const renderLink = (rowData: AirflowTaskDto) => {
    const linkTo = dag ? rowData.id.taskId : `${rowData.id.dagId}/${rowData.id.taskId}`;
    return <Link to={linkTo}>{rowData.title.trim() || "..."}</Link>;
  };

  const renderTaskId = (rowData: AirflowTaskDto) => <CollapsibleText text={rowData.id.taskId} />;

  const renderUserAvatar = (rowData: AirflowTaskDto) =>
    rowData.lastUpdatedBy && <UserAvatar username={rowData.lastUpdatedBy} />;

  const renderTaskType = (rowData: AirflowTaskDto) => {
    const type = airflowTaskTypes.find(t => t.value === rowData.type);
    return type ? <Tag severity={type.severity}>{type.label}</Tag> : <Tag>{rowData.type}</Tag>;
  };

  const renderSendAlerts = (rowData: AirflowTaskDto) => <CheckIcon enabled={rowData.sendAlerts} />;

  const renderStatus = (rowData: AirflowTaskDto) => (
    <div>
      {rowData.isSkipped && <i className="pi pi-power-off text-red-600" title="Task is skipped (disabled)" />}
      {rowData.showStepWarning && (
        <i
          className="pi pi-exclamation-triangle text-orange-600"
          title="Task is executing with more than 1 period steps!"
        />
      )}
    </div>
  );

  const filteredTasks = useMemo(() => {
    const filtered = (tasks ?? []).filter(task => {
      const matchesSearch = !filter.search || task.title.toLowerCase().includes(filter.search.toLowerCase());
      let matchesFilters = true;
      if (filter.filters.length > 0) {
        const groupFilters = filter.filters.reduce((acc: Record<string, unknown[]>, f) => {
          const key = Object.keys(f)[0] as keyof AirflowTaskDto;
          const value = f[key];
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(value);
          return acc;
        }, {});
        matchesFilters = Object.entries(groupFilters).every(([key, values]) =>
          values.includes(task[key as keyof AirflowTaskDto])
        );
      }
      return matchesSearch && matchesFilters;
    });

    return filtered;
  }, [tasks, filter]);

  const columns: TableColumn<AirflowTaskDto>[] = [
    {
      field: "id.taskId",
      header: T("word.task-id"),
      columnKey: "taskId",
      body: renderTaskId,
      sortable: true,
    },
    {
      field: "title",
      header: T("word.title"),
      body: renderLink,
      columnKey: "title",
      sortable: true,
    },
    {
      field: "description",
      header: T("word.description"),
      style: { maxWidth: "30rem", wordBreak: "break-word" },
      columnKey: "description",
    },
    {
      field: "upstreamTasks",
      header: T("word.invoked-by"),
      columnKey: "upstreamTasks",
      body: rowData => renderTaskTags(rowData.upstreamTasks, "up"),
    },
    {
      field: "downstreamTasks",
      header: T("word.invokes"),
      columnKey: "downstreamTasks",
      body: rowData => renderTaskTags(rowData.downstreamTasks, "down"),
    },
    {
      field: "lastUpdated",
      header: T("word.updated-at"),
      body: rowData => rowData.lastUpdated && fmtDatetime(rowData.lastUpdated, tz.display),
      columnKey: "lastUpdated",
      sortable: true,
    },
    {
      field: "lastUpdatedBy",
      header: T("word.updated-by"),
      body: renderUserAvatar,
      columnKey: "lastUpdatedBy",
      sortable: true,
    },
    {
      field: "type",
      header: T("word.type"),
      columnKey: "type",
      body: renderTaskType,
    },
    {
      field: "sendAlerts",
      header: T("word.sends-alerts"),
      columnKey: "sendAlerts",
      headerStyle: { justifyItems: "center" },
      body: renderSendAlerts,
      style: { textAlign: "center" },
    },
    {
      header: T("word.status"),
      columnKey: "status",
      body: renderStatus,
      style: { justifyItems: "center" },
    },
    {
      header: T("word.actions"),
      columnKey: "actions",
      body: renderActions,
      style: { justifyItems: "center" },
      hidden: !dag || !canEdit,
    },
  ];

  const tableConfig: DataTableProps<AirflowTaskDto[]> = {
    loading: loading,
    header: <TasksHeader dag={dag} filter={filter} setFilter={setFilter} />,
    rows: 25,
  };

  return <DataTable data={filteredTasks} tableConfig={tableConfig} columns={columns} />;
};

export default AirflowTaskTable;
